import {
  u2000SumHonorariumDayTotals,
  type U2000HonorariumDayTotals,
} from '../../u2000Core/tools/U2000TimesheetHonorariumDayTotals';
import type { U3129FindProjectItem, U3129FindSubprojectItem, U3129HonorariumItem } from '../U3129Service';
import { u3129ClientDisplayFromHonorariumRow, u3129ProjectDisplayLabel } from './u3129BuildHonorariumLinesFromTasks';

export const U3129_HONORARIUM_NO_PROJECT_KEY = '__sans_projet__';
export const U3129_HONORARIUM_NO_SUBPROJECT_KEY = '__sans_sous_projet__';

export type U3129HonorariumLineRef = {
  index: number;
  row: U3129HonorariumItem;
};

export type U3129HonorariumSubprojectGroup = {
  subprojectKey: string;
  subprojectId: string | null;
  subProjetNumber?: string;
  subProjetName?: string;
  lines: U3129HonorariumLineRef[];
  totals: U2000HonorariumDayTotals;
};

export type U3129HonorariumProjectGroup = {
  projectKey: string;
  projectId: string | null;
  projetNumber?: string;
  projetName?: string;
  sampleRow: U3129HonorariumItem | null;
  subprojectGroups: U3129HonorariumSubprojectGroup[];
  allLines: U3129HonorariumLineRef[];
  totals: U2000HonorariumDayTotals;
  includeSubProject: boolean;
};

function resolveProjectId(row: U3129HonorariumItem): string {
  return row.projectId?.trim() || row.project?.id?.trim() || '';
}

function resolveSubprojectId(row: U3129HonorariumItem): string {
  const sid = row.subprojectId?.trim();
  if (sid) return sid;
  const sp = (row as unknown as Record<string, unknown>).subproject as U3129FindSubprojectItem | undefined;
  return sp?.id?.trim() ?? '';
}

export function u3129HonorariumRowIncludeSubProject(row: U3129HonorariumItem): boolean {
  const project = (row as unknown as Record<string, unknown>).project as U3129FindProjectItem | undefined;
  const v = project?.includeSubProjet;
  return v === true || (typeof v === 'string' && v === 'true');
}

export function u3129ResolveHonorariumProjectKey(row: U3129HonorariumItem): string {
  const pid = resolveProjectId(row);
  return pid || U3129_HONORARIUM_NO_PROJECT_KEY;
}

function compareSubprojectGroups(a: U3129HonorariumSubprojectGroup, b: U3129HonorariumSubprojectGroup): number {
  if (a.subprojectKey === U3129_HONORARIUM_NO_SUBPROJECT_KEY) return 1;
  if (b.subprojectKey === U3129_HONORARIUM_NO_SUBPROJECT_KEY) return -1;
  const na = (a.subProjetNumber ?? '').trim();
  const nb = (b.subProjetNumber ?? '').trim();
  const c = na.localeCompare(nb, undefined, { numeric: true, sensitivity: 'base' });
  if (c !== 0) return c;
  const na2 = (a.subProjetName ?? '').trim();
  const nb2 = (b.subProjetName ?? '').trim();
  const c2 = na2.localeCompare(nb2, undefined, { numeric: true, sensitivity: 'base' });
  if (c2 !== 0) return c2;
  return (a.subprojectId ?? '').localeCompare(b.subprojectId ?? '');
}

function honorariumTaskSortLabel(row: U3129HonorariumItem): string {
  const desc = row.task?.description?.trim();
  if (desc) {
    return desc;
  }
  return (row.taskType ?? '').trim();
}

function sortHonorariumLineRefsByTaskLabel(lineRefs: U3129HonorariumLineRef[]): U3129HonorariumLineRef[] {
  return [...lineRefs].sort((a, b) => {
    const c = honorariumTaskSortLabel(a.row).localeCompare(honorariumTaskSortLabel(b.row), undefined, {
      sensitivity: 'base',
    });
    if (c !== 0) {
      return c;
    }
    return a.index - b.index;
  });
}

function compareProjectGroups(a: U3129HonorariumProjectGroup, b: U3129HonorariumProjectGroup): number {
  if (a.projectKey === U3129_HONORARIUM_NO_PROJECT_KEY) return 1;
  if (b.projectKey === U3129_HONORARIUM_NO_PROJECT_KEY) return -1;
  const la = u3129HonorariumProjectHeaderLabels(a.sampleRow).projectLabel.trim();
  const lb = u3129HonorariumProjectHeaderLabels(b.sampleRow).projectLabel.trim();
  const c = la.localeCompare(lb, undefined, { sensitivity: 'base' });
  if (c !== 0) {
    return c;
  }
  return (a.projectId ?? '').localeCompare(b.projectId ?? '');
}

function subprojectMetaFromLine(row: U3129HonorariumItem): Pick<U3129HonorariumSubprojectGroup, 'subProjetNumber' | 'subProjetName'> {
  const sp = (row as unknown as Record<string, unknown>).subproject as U3129FindSubprojectItem | undefined;
  return {
    subProjetNumber: sp?.subProjetNumber,
    subProjetName: sp?.subProjetName,
  };
}

function buildSubprojectGroup(subprojectKey: string, lines: U3129HonorariumLineRef[]): U3129HonorariumSubprojectGroup {
  const sample = lines[0]?.row;
  const meta = sample ? subprojectMetaFromLine(sample) : {};
  return {
    subprojectKey,
    subprojectId: subprojectKey === U3129_HONORARIUM_NO_SUBPROJECT_KEY ? null : subprojectKey,
    ...meta,
    lines: sortHonorariumLineRefsByTaskLabel(lines),
    totals: u2000SumHonorariumDayTotals(lines.map((l) => l.row)),
  };
}

export function u3129HonorariumProjectHeaderLabels(row: U3129HonorariumItem | null): { projectLabel: string; clientLabel: string } {
  if (!row) {
    return { projectLabel: '—', clientLabel: '—' };
  }
  const rowRec = row as unknown as Record<string, unknown>;
  const project = rowRec.project as U3129FindProjectItem | undefined;
  const rawProjectDisplay = rowRec.projectDisplay;
  const projectLabel =
    rawProjectDisplay != null && String(rawProjectDisplay).trim() !== ''
      ? String(rawProjectDisplay)
      : u3129ProjectDisplayLabel(project ?? null, row.projectId) || '—';
  const clientResolved = u3129ClientDisplayFromHonorariumRow(row);
  const clientLabel = clientResolved !== '' ? clientResolved : '—';
  return { projectLabel, clientLabel };
}

export function u3129HonorariumSubprojectHeaderLabel(group: U3129HonorariumSubprojectGroup): string {
  if (group.subprojectKey === U3129_HONORARIUM_NO_SUBPROJECT_KEY) {
    return '—';
  }
  const num = (group.subProjetNumber ?? '').trim();
  const name = (group.subProjetName ?? '').trim();
  const combined = [num, name].filter(Boolean).join(' ').trim();
  return combined || group.subprojectId || '—';
}

export function u3129GroupHonorariumForDisplay(
  rows: U3129HonorariumItem[],
  options?: { showSubProjectColumn?: boolean },
): U3129HonorariumProjectGroup[] {
  const showSub = options?.showSubProjectColumn ?? false;
  const byProject = new Map<string, U3129HonorariumLineRef[]>();

  rows.forEach((row, index) => {
    const key = u3129ResolveHonorariumProjectKey(row);
    const list = byProject.get(key) ?? [];
    list.push({ index, row });
    byProject.set(key, list);
  });

  const groups: U3129HonorariumProjectGroup[] = [];

  for (const [projectKey, lines] of byProject.entries()) {
    const sample = lines[0]?.row ?? null;
    const project = sample ? ((sample as unknown as Record<string, unknown>).project as U3129FindProjectItem | undefined) : undefined;
    const projectId = projectKey === U3129_HONORARIUM_NO_PROJECT_KEY ? null : projectKey;
    const includeSubProject = sample ? u3129HonorariumRowIncludeSubProject(sample) : false;
    const useSubGroups = showSub && includeSubProject && projectKey !== U3129_HONORARIUM_NO_PROJECT_KEY;

    let subprojectGroups: U3129HonorariumSubprojectGroup[];
    if (useSubGroups) {
      const bySub = new Map<string, U3129HonorariumLineRef[]>();
      for (const lineRef of lines) {
        const sid = resolveSubprojectId(lineRef.row);
        const skey = sid || U3129_HONORARIUM_NO_SUBPROJECT_KEY;
        const subLines = bySub.get(skey) ?? [];
        subLines.push(lineRef);
        bySub.set(skey, subLines);
      }
      subprojectGroups = [...bySub.entries()]
        .map(([skey, subLines]) => buildSubprojectGroup(skey, subLines))
        .sort(compareSubprojectGroups);
    } else {
      subprojectGroups = [buildSubprojectGroup(U3129_HONORARIUM_NO_SUBPROJECT_KEY, lines)];
    }

    groups.push({
      projectKey,
      projectId,
      projetNumber: project?.projetNumber,
      projetName: project?.projetName,
      sampleRow: sample,
      subprojectGroups,
      allLines: lines,
      totals: u2000SumHonorariumDayTotals(lines.map((l) => l.row)),
      includeSubProject,
    });
  }

  groups.sort(compareProjectGroups);
  return groups;
}
